import { useState, type ReactNode } from 'react';
import { Box, Breadcrumbs, Group, NavLink, Text } from '@mantine/core';
import { FiChevronDown } from 'react-icons/fi';

export interface CollapsibleNavItem {
  value: string;
  label: string;
  icon?: ReactNode;
  children?: CollapsibleNavItem[];
}

interface CollapsibleNavProps {
  items: CollapsibleNavItem[];
  value: string;
  onChange: (value: string) => void;
  color?: string;
}

function findPath(
  items: CollapsibleNavItem[],
  value: string
): CollapsibleNavItem[] {
  let path: CollapsibleNavItem[] = [];
  items.some((item) => {
    if (item.value === value) {
      path = [item];
      return true;
    }
    const childPath = item.children ? findPath(item.children, value) : [];
    if (childPath.length) {
      path = [item, ...childPath];
      return true;
    }
    return false;
  });
  return path;
}

/**
 * Mobile-friendly navigation that collapses to a breadcrumb of the current selection
 */
export default function CollapsibleNav({
  items,
  value,
  onChange,
  color = 'var(--yellow)',
}: CollapsibleNavProps) {
  const [opened, setOpened] = useState(false);

  const path = findPath(items, value);
  const activeItem = path[path.length - 1];

  const select = (next: string) => {
    onChange(next);
    setOpened(false);
  };

  const renderItems = (list: CollapsibleNavItem[]): ReactNode =>
    list.map((item) =>
      item.children ? (
        <NavLink
          key={item.value}
          label={item.label}
          leftSection={item.icon}
          childrenOffset={28}
          defaultOpened
        >
          {renderItems(item.children)}
        </NavLink>
      ) : (
        <NavLink
          key={item.value}
          label={item.label}
          leftSection={item.icon}
          active={item.value === value}
          onClick={() => select(item.value)}
          color={color}
        />
      )
    );

  return (
    <Box>
      <Group
        justify="space-between"
        wrap="nowrap"
        pb={8}
        onClick={() => setOpened((isOpen) => !isOpen)}
        style={{ cursor: 'pointer', borderBottom: `2px solid ${color}` }}
      >
        <Group gap={6} wrap="nowrap" fw={700}>
          {activeItem?.icon}
          <Breadcrumbs separator=">" separatorMargin="xs">
            {path.map((item) => (
              <Text key={item.value}>{item.label}</Text>
            ))}
          </Breadcrumbs>
        </Group>
        <FiChevronDown
          size={16}
          style={{
            transform: opened ? 'rotate(180deg)' : undefined,
            transition: 'transform 150ms ease',
          }}
        />
      </Group>

      {opened && (
        <Box style={{ boxShadow: '0 0 8px var(--box-shadow)' }}>
          {renderItems(items)}
        </Box>
      )}
    </Box>
  );
}
